import { useEffect, useRef } from 'react';

import { captureException } from '#lib/sentry';
import { fetchZendeskToken, isZendeskReady, loadZendesk, whenZendeskReady } from '#lib/zendesk';

/**
 * Makes the Zendesk chat widget available to logged-in users only.
 *
 * The script is injected on the first login; after logout it cannot be unloaded, so the widget
 * is hidden instead. Zendesk keeps the authenticated session in its own storage, so `loginUser`
 * runs once per login; it re-invokes the callback on its own whenever the token expires. The
 * signed token carries name, email and user id — the agent sees those on the profile.
 *
 * @param {{id: string}|null|undefined} user - Session user, null while logged out
 * @returns {void}
 */
export default function useZendeskMessenger(user) {
	const loggedInUserId = useRef(null);
	const userId = user?.id;

	useEffect(() => {
		if (!userId) {
			// signOut ran `logoutUser`, so the next login must identify again — even for the same user.
			loggedInUserId.current = null;
			if (isZendeskReady()) window.zE('messenger', 'hide');
			return undefined;
		}

		loadZendesk();

		return whenZendeskReady(() => {
			window.zE('messenger', 'show');
			if (loggedInUserId.current === userId) return;

			loggedInUserId.current = userId;
			window.zE('messenger', 'loginUser', (callback) => {
				fetchZendeskToken()
					.then(callback)
					// The widget stays usable as an anonymous chat, so this must not surface
					// as a toast — but a silent failure would hide a broken support identity.
					.catch((error) => captureException(error, { tags: { section: 'zendesk' } }));
			});
		});
	}, [userId]);
}
